import { Box, TableSortLabel } from '@mui/material';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Pagination,
  LoadedContent,
  TableContainer,
  Typography,
} from '@occc/ui-design-system';
import {
  RetailerNetworkLocationWithPerformanceExcerptFragment,
  Retailer_Network_Performance_Sort_By,
  RetailerNetworkPerformanceSort,
} from '@occc/graphql';

import { RetailerNetworkLocationGroupLocationsTableRow } from './retailer-network-location-group-locations-table-row.component';

import { useGetRetailerNetworkLocationsWithPerformanceExcerpt } from '../../data-access/queries';
import { mapSortOrderToRetailerNetworkPerformanceSortDirection } from '../retailer-connect.utils';

type RetailerNetworkLocationGroupLocationsTableProps = {
  retailerNetworkUuid: string;
  locationsData: ReturnType<typeof useGetRetailerNetworkLocationsWithPerformanceExcerpt>;
  setRemoveLocationDialogOpen: (locationUuid: string | null) => void;
  handleOpenLocationDetailsPage: (locationUuid: string) => void;
  sortingParameters?: RetailerNetworkPerformanceSort;
  toggleSorting: (field: string) => void;
};

const RetailerNetworkLocationGroupLocationsTable = ({
  retailerNetworkUuid,
  locationsData,
  sortingParameters,
  setRemoveLocationDialogOpen,
  handleOpenLocationDetailsPage,
  toggleSorting,
}: RetailerNetworkLocationGroupLocationsTableProps) => {
  const { loading: locationsLoading, error, data, paginationProps } = locationsData;

  const isSortedByMembers =
    sortingParameters?.field === Retailer_Network_Performance_Sort_By.TotalNewMembers;
  const isSortedByTransaction =
    sortingParameters?.field === Retailer_Network_Performance_Sort_By.TotalTransactions;
  const newMembershipSortingDirection = isSortedByMembers
    ? mapSortOrderToRetailerNetworkPerformanceSortDirection[sortingParameters.direction]
    : 'desc';
  const transactionSortingDirection = isSortedByTransaction
    ? mapSortOrderToRetailerNetworkPerformanceSortDirection[sortingParameters.direction]
    : 'desc';

  const renderData = (items: RetailerNetworkLocationWithPerformanceExcerptFragment[]) => (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Site ID</TableCell>
            <TableCell>Site Name</TableCell>
            <TableCell align='right'>
              <TableSortLabel
                active={isSortedByMembers}
                sx={{ flexDirection: 'row-reverse' }}
                direction={newMembershipSortingDirection}
                onClick={() => toggleSorting(Retailer_Network_Performance_Sort_By.TotalNewMembers)}
              >
                New Members
              </TableSortLabel>
            </TableCell>
            <TableCell align='right'>
              <TableSortLabel
                active={isSortedByTransaction}
                sx={{ flexDirection: 'row-reverse' }}
                direction={transactionSortingDirection}
                onClick={() =>
                  toggleSorting(Retailer_Network_Performance_Sort_By.TotalTransactions)
                }
              >
                Transactions
              </TableSortLabel>
            </TableCell>
            <TableCell align='right'>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((location) => (
            <RetailerNetworkLocationGroupLocationsTableRow
              retailerNetworkUuid={retailerNetworkUuid}
              location={location}
              key={location.locationUuid}
              setRemoveLocationDialogOpen={setRemoveLocationDialogOpen}
              handleOpenLocationDetailsPage={handleOpenLocationDetailsPage}
            />
          ))}
        </TableBody>
      </Table>
      <Pagination {...paginationProps} />
    </TableContainer>
  );

  const renderEmpty = () => (
    <Box
      py={14}
      maxWidth={500}
      margin='0 auto'
      display='flex'
      alignItems='center'
      flexDirection='column'
    >
      <Typography variant='h6' align='center' paragraph>
        No locations available
      </Typography>
      <Typography align='center' color='textSecondary' paragraph>
        There are no locations assigned to this Location Group.
      </Typography>
    </Box>
  );

  return (
    <LoadedContent<RetailerNetworkLocationWithPerformanceExcerptFragment>
      error={error}
      loading={locationsLoading}
      items={data}
      renderData={renderData}
      renderEmpty={renderEmpty}
    />
  );
};

export default RetailerNetworkLocationGroupLocationsTable;
